(() => {
  'use strict';
  if (window.EvolvedSetupResume) return;
  const keys = ['evolved.setup.draft.v1', 'evolved.setup.samsung.draft.v1'];
  const intentKey = 'evolved.setup.resume-intent.v1';
  const dismissKey = key => key + '.dismissed';
  const parse = (storage, key) => { try { return JSON.parse(storage.getItem(key) || 'null'); } catch (_) { return null; } };
  const signature = draft => JSON.stringify((draft.items || []).map(item => [item.handle, String(item.variantId), item.category, !!item.cartOrigin]).sort((a, b) => a[2] - b[2]));
  const fingerprint = draft => JSON.stringify([signature(draft), draft.ready === true, draft.skipped || [], draft.repair || []]);
  function read(key) {
    try {
      const draft = parse(localStorage, key), age = Date.now() - draft?.savedAt;
      if (draft?.version !== 1 || !Number.isFinite(age) || age < 0 || age >= 30 * 86400000 || !Array.isArray(draft.items) || !draft.items.length || draft.items.length > 6) return null;
      if (!draft.items.every(item => item && /^[a-z0-9][a-z0-9-]*$/.test(item.handle) && /^\d+$/.test(String(item.variantId)) && Number.isInteger(item.category) && item.category >= 0 && item.category <= (key === keys[1] ? 4 : 5))) return null;
      if (new Set(draft.items.map(item => item.category)).size !== draft.items.length) return null;
      return draft;
    } catch (_) { return null; }
  }
  function takeIntent(key) {
    try {
      const url = new URL(location.href), id = url.searchParams.get('setup_resume');
      if (!id) return null;
      url.searchParams.delete('setup_resume');
      history.replaceState(history.state, '', url.href);
      const intent = parse(sessionStorage, intentKey);
      if (intent?.id !== id || intent.key !== key) return null;
      sessionStorage.removeItem(intentKey);
      const draft = read(key), age = Date.now() - intent.createdAt;
      return age >= 0 && age < 120000 && draft?.ready === true && !draft.pending && draft.items.length >= 2 && intent.signature === signature(draft) ? intent : null;
    } catch (_) { return null; }
  }
  window.EvolvedSetupResume = Object.freeze({ signature, takeIntent, changed: () => document.dispatchEvent(new CustomEvent('evolved:setup-draft-change')) });

  class SetupResume extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.row = this.closest('[data-ev-setup-resume-row]');
      this.header = this.closest('store-header');
      this.wrapper = this.header?.querySelector('.header__wrapper');
      this.nav = this.header?.querySelector('.header__link-list');
      this.link = this.querySelector('[data-resume-action]');
      this.label = this.querySelector('[data-resume-label]');
      this.count = this.querySelector('[data-resume-count]');
      this.close = this.querySelector('[data-resume-dismiss]');
      if (!this.row || !this.link || !this.close) return;
      const list = this.nav?.querySelector('ul');
      if (list?.lastElementChild) {
        this.inline = document.createElement('li');
        this.inline.className = 'ev-setup-resume-inline';
        this.inline.hidden = true;
        list.prepend(this.inline);
      }
      this.close.addEventListener('click', () => {
        if (!this.current) return;
        const nextFocus = this.header.querySelector('.header__logo a, a.header__logo, .header__secondary-nav a');
        this.dismissed ||= new Map();
        keys.forEach(key => {
          const draft = read(key);
          if (!draft) return;
          this.dismissed.set(key, fingerprint(draft));
          try { localStorage.setItem(dismissKey(key), JSON.stringify(fingerprint(draft))); } catch (_) {}
        });
        this.refresh();
        nextFocus?.focus({ preventScroll: true });
      }, options);
      this.link.addEventListener('click', event => this.activate(event), options);
      document.addEventListener('evolved:setup-draft-change', () => this.refresh(), options);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
      window.addEventListener('storage', event => { if (!event.key || keys.some(key => event.key === key || event.key === dismissKey(key))) this.refresh(); }, options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      window.addEventListener('resize', () => this.scheduleLayout(), options);
      document.fonts?.addEventListener('loadingdone', () => this.scheduleLayout(), options);
      if (this.wrapper && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect.width;
          if (width === this.wrapperWidth) return;
          this.wrapperWidth = width;
          this.scheduleLayout();
        });
        this.resizeObserver.observe(this.wrapper);
      }
      this.refresh();
    }
    refresh() {
      this.activating = false;
      this.link.removeAttribute('aria-disabled');
      this.current = keys.map(key => ({ key, draft: read(key) })).filter(item => {
        if (!item.draft || !this.dataset[item.key === keys[1] ? 'samsungUrl' : 'iphoneUrl']) return false;
        let dismissed = this.dismissed?.get(item.key);
        try { dismissed ||= parse(localStorage, dismissKey(item.key)); } catch (_) {}
        const changed = dismissed !== fingerprint(item.draft);
        if (dismissed && changed) {
          this.dismissed?.delete(item.key);
          try { localStorage.removeItem(dismissKey(item.key)); } catch (_) {}
        }
        return changed;
      }).sort((a, b) => b.draft.savedAt - a.draft.savedAt)[0] || null;
      this.hidden = !this.current;
      if (!this.current) { this.row.hidden = true; if (this.inline) this.inline.hidden = true; return; }
      const { key, draft } = this.current;
      const ready = draft.ready === true && draft.items.length >= 2 && !draft.pending;
      const label = ready ? this.dataset.addLabel : this.dataset.continueLabel;
      this.label.textContent = label;
      this.count.textContent = (draft.items.length === 1 ? this.dataset.oneItem : this.dataset.manyItems).replace('[count]', String(draft.items.length));
      this.link.href = this.dataset[key === keys[1] ? 'samsungUrl' : 'iphoneUrl'];
      this.link.setAttribute('aria-label', label + ' · ' + this.count.textContent + ' · ' + (key === keys[1] ? 'Samsung' : 'Apple'));
      this.scheduleLayout();
    }
    activate(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (this.activating) { event.preventDefault(); return; }
      const current = this.current, draft = current && read(current.key);
      if (!draft) { event.preventDefault(); this.refresh(); return; }
      const target = new URL(this.link.href, location.origin);
      if (target.origin !== location.origin || !/\/pages\/build-your-(?:samsung-)?setup\/?$/.test(target.pathname)) { event.preventDefault(); return; }
      if (draft.ready === true && draft.items.length >= 2 && !draft.pending) {
        try {
          const id = crypto.randomUUID();
          sessionStorage.setItem(intentKey, JSON.stringify({ id, key: current.key, createdAt: Date.now(), signature: signature(draft), quote: draft.quote || null }));
          target.searchParams.set('setup_resume', id);
          this.link.href = target.href;
        } catch (_) {}
      }
      this.activating = true;
      this.link.setAttribute('aria-disabled', 'true');
      this.label.textContent = this.dataset.loadingLabel;
    }
    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.place());
    }
    place() {
      if (this.hidden || !this.isConnected) return;
      const desktop = matchMedia('(min-width: 1150px)').matches && this.inline && this.nav?.getClientRects().length;
      if (!desktop) { this.placeBelow(); return; }
      this.row.hidden = true;
      this.inline.hidden = false;
      this.inline.setAttribute('data-measuring', '');
      this.inline.setAttribute('data-floating', '');
      if (this.parentElement !== this.inline) this.inline.append(this);

      // Measure the original menu without making the reminder part of its width.
      const nav = this.nav.getBoundingClientRect();
      const logo = this.header.querySelector('.header__logo')?.getBoundingClientRect();
      const secondary = this.header.querySelector('.header__secondary-nav')?.getBoundingClientRect();
      const reminder = this.inline.getBoundingClientRect();
      const navStyle = getComputedStyle(this.nav);
      const rtl = navStyle.direction === 'rtl';
      const gap = parseFloat(navStyle.columnGap) || 16;
      const left = rtl ? nav.right + gap : nav.left - gap - reminder.width;
      const fits = logo && secondary && (rtl
        ? left + reminder.width <= logo.left - 16 && nav.left >= secondary.right + 16
        : left >= logo.right + 16 && nav.right <= secondary.left - 16);

      if (fits) {
        const parent = this.inline.offsetParent;
        if (!parent) { this.placeBelow(); return; }
        const origin = parent.getBoundingClientRect();
        const start = rtl
          ? origin.left + parent.clientLeft + parent.clientWidth - left - reminder.width
          : left - origin.left - parent.clientLeft;
        this.inline.style.setProperty('--resume-inline-start', start + 'px');
        this.inline.style.setProperty('--resume-inline-top', (nav.top + (nav.height - reminder.height) / 2 - origin.top - parent.clientTop) + 'px');
        this.dataset.placement = 'floating';
      } else {
        // Only let the reminder participate in menu layout when spare space runs out.
        this.inline.removeAttribute('data-floating');
        this.dataset.placement = 'inline';
        const placed = this.nav.getBoundingClientRect();
        const items = [...this.nav.querySelector('ul').children].filter(node => node.getClientRects().length);
        const centers = items.map(node => { const box = node.getBoundingClientRect(); return box.top + box.height / 2; });
        const collision = !logo || !secondary || (rtl
          ? placed.right > logo.left - 16 || placed.left < secondary.right + 16
          : placed.left < logo.right + 16 || placed.right > secondary.left - 16)
          || Math.max(...centers) - Math.min(...centers) > 8 || this.nav.scrollWidth > this.nav.clientWidth + 1;
        if (collision) { this.placeBelow(); return; }
      }
      this.inline.removeAttribute('data-measuring');
    }
    placeBelow() {
      if (this.inline) {
        this.inline.hidden = true;
        this.inline.removeAttribute('data-floating');
        this.inline.removeAttribute('data-measuring');
      }
      this.row.hidden = false;
      if (this.parentElement !== this.row) this.row.append(this);
      this.dataset.placement = 'below';
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        cancelAnimationFrame(this.layoutFrame);
        this.resizeObserver?.disconnect();
        this.controller?.abort();
        this.controller = null;
        this.inline?.remove();
      });
    }
  }
  if (!customElements.get('evolved-setup-resume')) customElements.define('evolved-setup-resume', SetupResume);
})();
